import React from "react";
import { Link } from "react-router-dom";
import PublicPage from "@/components/public/PublicPage";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "@/lib/app-links";

type Section = { heading: string; points: string[] };
type Landing = {
  lang?: string;
  eyebrow: string;
  title: string;
  intro: string;
  sections: Section[];
  priceNote: string;
  cta: string;
  storeLabel: string;
};

function LandingTemplate({ d }: { d: Landing }) {
  return (
    <div lang={d.lang}>
      <PublicPage eyebrow={d.eyebrow} title={d.title} intro={d.intro}>
        <section className="relative">
          <div className="max-w-6xl mx-auto px-4 pb-20 space-y-8">
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-crystal-orange hover:bg-crystal-orange/90">
                <Link to="/auth?mode=signup">{d.cta}</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/pricing">{d.priceNote}</Link>
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {d.sections.map((s) => (
                <div key={s.heading} className="app-card p-6">
                  <h2 className="text-white font-semibold text-xl mb-3">{s.heading}</h2>
                  <ul className="space-y-2">
                    {s.points.map((p) => (
                      <li key={p} className="flex gap-2 text-white/75 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-crystal-orange shrink-0 mt-0.5" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="text-white/65 text-sm">
              {d.storeLabel}{" "}
              <a href={APP_STORE_URL} className="underline hover:text-white" target="_blank" rel="noopener noreferrer">App Store</a>
              {" · "}
              <a href={GOOGLE_PLAY_URL} className="underline hover:text-white" target="_blank" rel="noopener noreferrer">Google Play</a>
            </p>
          </div>
        </section>
      </PublicPage>
    </div>
  );
}

const ikMat: Landing = {
  lang: "nb",
  eyebrow: "IK-mat app",
  title: "Digital internkontroll for mat (IK-mat) – på mobil og web",
  intro: "Crystal HACCP er laget i Oslo for restauranter, kafeer og catering. Før temperaturlogger, renholdssjekker og avvik digitalt, og ha dokumentasjonen klar når Mattilsynet kommer på tilsyn.",
  sections: [
    { heading: "Daglige rutiner", points: ["Temperaturlogg for kjøl, frys og varmholding", "Åpnings- og stengesjekklister klare fra første dag", "Renhold, skadedyr og mottakskontroll"] },
    { heading: "Avvik og korrigerende tiltak", points: ["Registrer avvik med bilde, tale eller video", "Følg opp tiltak til de er lukket", "Full historikk med hvem som gjorde hva og når"] },
    { heading: "Klar for tilsyn", points: ["Eksporter rapporter som PDF med signaturer", "Regionale regelpakker per lokasjon", "Alt samlet på ett sted i stedet for permer"] },
    { heading: "For hele teamet", points: ["App for iPhone, iPad og Android", "Web-dashboard for daglig leder", "Flere lokasjoner i samme oversikt"] },
  ],
  priceNote: "Se priser (fra 34,99 USD per enhet/mnd)",
  cta: "Start 7 dagers gratis prøveperiode",
  storeLabel: "Last ned appen:",
};

const haccpApp: Landing = {
  eyebrow: "HACCP app for restaurants",
  title: "The HACCP app for restaurants, cafes and catering",
  intro: "Replace paper food-safety diaries with a phone app your team actually uses. Temperature logs, cleaning checks, corrective actions and audit-ready reports, connected to a web dashboard.",
  sections: [
    { heading: "Daily checks in minutes", points: ["Fridge, freezer and hot-holding temperature logs", "Opening and closing checklists ready on day one", "Scheduled checks for every unit"] },
    { heading: "Problems get fixed", points: ["Log incidents with photo, voice or video evidence", "Track corrective actions until they are closed", "Full audit trail of who did what and when"] },
    { heading: "Ready for inspection", points: ["PDF reports with signatures", "Regional law packs per location", "Every record in one place"] },
    { heading: "Web and mobile", points: ["iPhone, iPad and Android app", "Web dashboard for managers", "Multi-location overview"] },
  ],
  priceNote: "See pricing (from $34.99 per unit/month)",
  cta: "Start your 7-day free trial",
  storeLabel: "Download the app:",
};

const sfbb: Landing = {
  eyebrow: "SFBB alternative",
  title: "A digital alternative to the Safer Food, Better Business pack",
  intro: "Keep the daily diary, temperature records and cleaning checks your food-safety management system needs, without the paper binder. Built for UK restaurants, cafes and takeaways.",
  sections: [
    { heading: "Your daily diary, on a phone", points: ["Opening and closing checks", "Fridge, freezer and cooking temperature records", "Cleaning and pest checks"] },
    { heading: "When something goes wrong", points: ["Record what happened and what you did about it", "Attach photos as evidence", "Corrective actions tracked until closed"] },
    { heading: "When the inspector visits", points: ["Export records as a PDF", "Signed-off checklists with time stamps", "History kept, not lost in a folder"] },
    { heading: "Switching is simple", points: ["Starter checklists are added for you", "Import your existing documents", "7-day free trial, no card needed to start"] },
  ],
  priceNote: "See pricing (from $34.99 per unit/month)",
  cta: "Start your 7-day free trial",
  storeLabel: "Download the app:",
};

const tempLog: Landing = {
  eyebrow: "Temperature log",
  title: "Digital fridge and freezer temperature log",
  intro: "Record fridge, freezer, delivery and hot-holding temperatures twice a day from a phone. Out-of-range readings prompt a corrective action, and every entry is saved with time and name.",
  sections: [
    { heading: "What gets recorded", points: ["Unit, reading, time and who logged it", "Morning and evening checks scheduled automatically", "Notes and photos when needed"] },
    { heading: "When a reading is wrong", points: ["Flag the deviation", "Record the corrective action", "Follow it up until closed"] },
    { heading: "Reports", points: ["Export temperature history as PDF", "Signed checklists", "Records for every location in one dashboard"] },
    { heading: "Get started", points: ["A daily fridge and freezer checklist is created for you at signup", "Works on iPhone, iPad, Android and web", "7-day free trial"] },
  ],
  priceNote: "See pricing (from $34.99 per unit/month)",
  cta: "Start your 7-day free trial",
  storeLabel: "Download the app:",
};

export const IkMatPage = () => <LandingTemplate d={ikMat} />;
export const HaccpAppPage = () => <LandingTemplate d={haccpApp} />;
export const SfbbPage = () => <LandingTemplate d={sfbb} />;
export const TemperatureLogPage = () => <LandingTemplate d={tempLog} />;
